
import React from 'react';
import { UserProfile, CompanionStage, Theme } from '../types';
import { GlassCard, Button, ProgressBar } from '../components/shared';
import { CompanionAvatar } from '../components/Companion';
import { X, Zap, Trophy, History, Dna, Sparkles } from 'lucide-react';
import { translations } from '../i18n';

interface CompanionStatusProps {
  user: UserProfile;
  lang: 'en' | 'zh';
  theme: Theme;
  onClose: () => void;
}

const STAGE_CONFIG = {
  [CompanionStage.EGG]: { min: 1, max: 3, label: { en: 'Stardust Egg', zh: '星尘之卵' }, desc: { en: 'Sleeping in potential.', zh: '沉睡在无限的可能性中。' } },
  [CompanionStage.BABY]: { min: 3, max: 6, label: { en: 'Spark Spirit', zh: '火花幼灵' }, desc: { en: 'Curious about the world.', zh: '对世界充满好奇的小家伙。' } },
  [CompanionStage.FLYING]: { min: 6, max: 10, label: { en: 'Aero Navigator', zh: '御风领航员' }, desc: { en: 'Exploring the skies.', zh: '已具备探索星空的能力。' } },
  [CompanionStage.MASTER]: { min: 10, max: 999, label: { en: 'Cosmic Sage', zh: '宇宙贤者' }, desc: { en: 'Master of knowledge.', zh: '掌握了星际真理的智者。' } },
};

export const CompanionStatus: React.FC<CompanionStatusProps> = ({ user, lang, theme, onClose }) => {
  const t = translations[lang];
  const isDark = theme === 'dark';

  const currentStage = 
    user.level < 3 ? CompanionStage.EGG :
    user.level < 6 ? CompanionStage.BABY :
    user.level < 10 ? CompanionStage.FLYING : CompanionStage.MASTER;
    
  const nextStageLevel = 
    currentStage === CompanionStage.EGG ? 3 :
    currentStage === CompanionStage.BABY ? 6 :
    currentStage === CompanionStage.FLYING ? 10 : 999;

  const prevStageLevel = 
    currentStage === CompanionStage.EGG ? 0 :
    currentStage === CompanionStage.BABY ? 3 :
    currentStage === CompanionStage.FLYING ? 6 : 10;

  const levelsInStage = nextStageLevel - prevStageLevel;
  const levelsGained = user.level - prevStageLevel;
  const stageProgress = Math.min(100, Math.max(0, (levelsGained / levelsInStage) * 100));

  const xpForNextLevel = 200;
  const currentLevelXp = user.xp % 200;

  // Theme-aware styles
  const bgOverlay = isDark ? 'bg-slate-950/80' : 'bg-slate-200/60';
  const podBg = isDark ? 'from-indigo-900/50 to-slate-900/50' : 'from-indigo-100/50 to-white/50';
  const dataPanelBg = isDark ? 'bg-transparent' : 'bg-white/40';
  const textPrimary = isDark ? 'text-white' : 'text-slate-900';
  const textSecondary = isDark ? 'text-slate-400' : 'text-slate-500';
  const statsBox = isDark ? 'bg-white/5 border-white/5' : 'bg-slate-100/80 border-slate-200';

  return (
    <div className={`fixed inset-0 z-[60] flex items-center justify-center backdrop-blur-xl p-4 animate-in fade-in duration-300 ${bgOverlay}`}>
      
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
         <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-indigo-500/10 rounded-full blur-[100px] animate-pulse-slow"></div>
      </div>

      <GlassCard 
        variant={isDark ? 'dark' : 'light'} 
        className={`w-full max-w-2xl relative overflow-hidden border shadow-2xl p-0 flex flex-col md:flex-row min-h-[500px] ${isDark ? 'border-white/20' : 'border-slate-300'}`}
      >
        
        <button onClick={onClose} className={`absolute top-4 right-4 z-20 p-2 rounded-full transition-colors ${isDark ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'}`}>
          <X size={20} />
        </button>

        {/* LEFT COLUMN: The Pod */}
        <div className={`w-full md:w-5/12 bg-gradient-to-b ${podBg} p-8 flex flex-col items-center justify-center relative border-b md:border-b-0 md:border-r ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
           <div className={`absolute w-64 h-64 border-2 rounded-full animate-[spin_10s_linear_infinite] opacity-50 ${isDark ? 'border-indigo-400/30' : 'border-indigo-200'}`}></div>
           <div className={`absolute w-56 h-56 border rounded-full animate-[spin_15s_linear_infinite_reverse] opacity-50 ${isDark ? 'border-white/10' : 'border-slate-200'}`}></div>
           
           <div className="relative z-10 transform hover:scale-110 transition-transform duration-500 cursor-pointer">
              <div className="absolute inset-0 bg-indigo-400 blur-[50px] opacity-40 animate-pulse"></div>
              <CompanionAvatar level={user.level} size="lg" talking />
           </div>

           <div className="mt-8 text-center relative z-10">
              <h2 className={`text-2xl font-black font-orbitron mb-1 drop-shadow-lg ${textPrimary}`}>Nono</h2>
              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-bold uppercase tracking-widest ${isDark ? 'bg-indigo-500/20 border-indigo-400/30 text-indigo-200' : 'bg-indigo-100 border-indigo-200 text-indigo-600'}`}>
                <Sparkles size={10} />
                {lang === 'zh' ? STAGE_CONFIG[currentStage].label.zh : STAGE_CONFIG[currentStage].label.en}
              </div>
           </div>
        </div>

        {/* RIGHT COLUMN: Data & Evolution */}
        <div className={`flex-1 p-8 flex flex-col ${dataPanelBg}`}>
           
           <h3 className={`text-sm font-bold uppercase tracking-widest mb-6 flex items-center gap-2 ${textSecondary}`}>
              <Dna size={16} className="text-indigo-400"/> {lang === 'zh' ? '神经连接状态' : 'Neural Link Status'}
           </h3>

           {/* Stats Grid */}
           <div className="grid grid-cols-2 gap-4 mb-8">
              <div className={`rounded-2xl p-4 border ${statsBox}`}>
                 <div className={`text-xs font-bold uppercase mb-1 ${textSecondary}`}>{t.level}</div>
                 <div className={`text-3xl font-black font-orbitron ${textPrimary}`}>{user.level}</div>
              </div>
              <div className={`rounded-2xl p-4 border ${statsBox}`}>
                 <div className={`text-xs font-bold uppercase mb-1 flex items-center gap-1 ${textSecondary}`}><History size={10}/> Streak</div>
                 <div className="text-3xl font-black text-emerald-500 font-orbitron">{user.streak} <span className="text-xs font-bold text-slate-400">days</span></div>
              </div>
           </div>

           {/* XP Progress */}
           <div className="mb-8">
              <div className="flex justify-between text-xs font-bold mb-2">
                 <span className={`${isDark ? 'text-indigo-200' : 'text-indigo-600'}`}>{lang === 'zh' ? '当前等级经验' : 'Level Progress'}</span>
                 <span className={textPrimary}>{currentLevelXp} / {xpForNextLevel} XP</span>
              </div>
              <ProgressBar current={currentLevelXp} max={xpForNextLevel} color="bg-gradient-to-r from-indigo-500 to-purple-500" className={`h-4 ${isDark ? 'bg-slate-800' : 'bg-slate-200'}`} />
           </div>

           {/* Evolution Timeline */}
           <div className="flex-1">
              <div className="flex justify-between items-center mb-4">
                 <span className={`text-xs font-bold uppercase tracking-widest ${textSecondary}`}>{lang === 'zh' ? '进化序列' : 'Evolution Sequence'}</span>
                 {currentStage !== CompanionStage.MASTER && (
                   <span className="text-[10px] font-bold text-yellow-600 bg-yellow-400/10 px-2 py-1 rounded border border-yellow-400/20">
                      {lang === 'zh' ? `距离进化还差 ${nextStageLevel - user.level} 级` : `${nextStageLevel - user.level} levels to evolve`}
                   </span>
                 )}
              </div>
              
              <div className="relative pt-2">
                 <div className={`absolute top-1/2 left-0 w-full h-1 rounded-full -translate-y-1/2 ${isDark ? 'bg-slate-800' : 'bg-slate-200'}`}></div>
                 <div 
                    className="absolute top-1/2 left-0 h-1 bg-indigo-500 rounded-full -translate-y-1/2 transition-all duration-1000 shadow-[0_0_10px_rgba(99,102,241,0.5)]"
                    style={{ width: `${stageProgress}%` }}
                 ></div>

                 <div className="relative flex justify-between">
                    {[CompanionStage.EGG, CompanionStage.BABY, CompanionStage.FLYING, CompanionStage.MASTER].map((stage, idx) => {
                       const isPast = STAGE_CONFIG[stage].min < user.level;
                       const isCurrent = stage === currentStage;
                       
                       return (
                          <div key={stage} className="flex flex-col items-center gap-2 group cursor-help relative">
                             <div className={`w-4 h-4 rounded-full border-2 z-10 transition-all duration-500 ${isCurrent ? 'bg-indigo-500 border-white scale-125 shadow-[0_0_15px_rgba(99,102,241,1)]' : isPast ? 'bg-indigo-900 border-indigo-500' : isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-300'}`}></div>
                             <span className={`text-[10px] font-bold uppercase transition-colors ${isCurrent ? textPrimary : textSecondary}`}>
                                {stage.substring(0, 3)}
                             </span>
                          </div>
                       )
                    })}
                 </div>
              </div>
              
              <p className={`mt-6 text-xs italic text-center border-t pt-4 ${isDark ? 'text-slate-400 border-white/5' : 'text-slate-500 border-slate-100'}`}>
                 "{lang === 'zh' ? STAGE_CONFIG[currentStage].desc.zh : STAGE_CONFIG[currentStage].desc.en}"
              </p>
           </div>

        </div>

      </GlassCard>
    </div>
  );
};
